import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import FundingClientRosterTable from '../../components/funding/workspace/FundingClientRosterTable';
import FundingClientDetailsView from '../../components/funding/workspace/FundingClientDetailsView';
import FundingClientFilters from '../../components/funding/FundingClientFilters';
import FundingPanel from '../../components/funding/FundingPanel';
import FundingSectionPage from '../../components/funding/FundingSectionPage';
import FundingRosterPagination from '../../components/funding/workspace/FundingRosterPagination';
import FundingSampleBanner from '../../components/funding/workspace/FundingSampleBanner';
import NewFundingClientModal from '../../components/funding/workspace/NewFundingClientModal';
import ManualFundingClientDrawer from '../../components/funding/workspace/ManualFundingClientDrawer';
import UploadDocumentsDrawer from '../../components/funding/workspace/UploadDocumentsDrawer';
import PasteClientInfoDrawer from '../../components/funding/workspace/PasteClientInfoDrawer';
import BulkImportClientsModal from '../../components/funding/workspace/BulkImportClientsModal';
import {
  FundingClientDetailsSkeleton,
  FundingRosterTableSkeleton,
  useSkeletonFailsafe,
} from '../../components/funding/workspace/FundGeniusSkeletons';
import { useAuth } from '../../context/AuthContext';
import { useFundingWorkspace } from '../../context/FundingWorkspaceContext';
import { usePlatformAgencies } from '../../hooks/funding/usePlatformAgencies';
import {
  FUNDING_SESSION_EXPIRED_MESSAGE,
  isFundingAuthError,
} from '../../services/funding/fundingApi';
import {
  createFundingClient,
  deleteFundingClient,
  extractedClientDataToCreatePayload,
  fetchFundingClient,
  fetchFundingClients,
  manualIntakeFormToCreatePayload,
  manualIntakeFormToUpdatePayload,
  normalizeFundingClient,
  resolveIntakeSource,
  updateFundingClient,
} from '../../services/funding/fundingClientsService';
import {
  deleteFundingClientDraft,
  listFundingClientDrafts,
  saveFundingClientDraft,
  formatDraftSourceLabel,
} from '../../services/funding/fundingClientDraftsService';
import {
  clearAddClientSessionDraft,
  loadAddClientSessionDraft,
  saveAddClientSessionDraft,
} from '../../services/funding/fundingAddClientSessionDraft';
import { redirectToLoginForExpiredSession } from '../../services/authSession';
import { isFrontendDevAuthBypassEnabled } from '../../utils/devAuth';
import {
  DEV_MOCK_FUNDING_CLIENTS,
  DEV_MOCK_ROSTER_STATS,
} from '../../utils/devFundingMock';
import {
  clientToManualIntakeForm,
  emptyManualIntakeForm,
} from '../../utils/fundingClientIntakeUtils';
import {
  filterRealFundingClients,
  hasOnlySampleClients,
  hasRealClients,
  isFundingClientsSampleUiEnabled,
  loadClientsSampleMode,
  saveClientsSampleMode,
} from '../../utils/fundingSampleMode';

const EMPTY_UPLOAD_SESSION = [];

const sampleUiEnabled = isFundingClientsSampleUiEnabled();
const initialSampleMode = sampleUiEnabled ? loadClientsSampleMode(true) : false;
const bootstrapSampleClients = sampleUiEnabled && initialSampleMode;

const FundingClientsPage = () => {
  const { token, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { clientId: routeClientId } = useParams();
  const {
    isPlatformContext,
    workspaceAgencyId,
    setWorkspaceAgencyId,
    requiresAgencySelection,
    pathFor,
    clients: clientsPath,
  } = useFundingWorkspace();

  const bootstrapClientFromRoute = bootstrapSampleClients && routeClientId
    ? DEV_MOCK_FUNDING_CLIENTS.find((client) => client.id === routeClientId) ?? null
    : null;

  const [clients, setClients] = useState(() => (
    bootstrapSampleClients ? DEV_MOCK_FUNDING_CLIENTS : []
  ));
  const [isFetchingClients, setIsFetchingClients] = useState(!bootstrapSampleClients);
  const [hasResolvedClients, setHasResolvedClients] = useState(bootstrapSampleClients);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [rosterStats, setRosterStats] = useState(DEV_MOCK_ROSTER_STATS);
  const [clientsSampleMode, setClientsSampleMode] = useState(initialSampleMode);

  const [viewMode, setViewMode] = useState(bootstrapClientFromRoute ? 'details' : 'roster');
  const [selectedClient, setSelectedClient] = useState(bootstrapClientFromRoute);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const [newClientModalOpen, setNewClientModalOpen] = useState(false);
  const pendingNewClientOpenRef = useRef(false);
  const skipUploadSessionClearRef = useRef(false);
  const [manualDrawerOpen, setManualDrawerOpen] = useState(false);
  const [uploadDrawerOpen, setUploadDrawerOpen] = useState(false);
  const [pasteDrawerOpen, setPasteDrawerOpen] = useState(false);
  const [pasteInitialText, setPasteInitialText] = useState('');
  const [uploadSession, setUploadSession] = useState(null);
  const [bulkImportOpen, setBulkImportOpen] = useState(false);
  const [manualInitialValues, setManualInitialValues] = useState(null);
  const [editingClientId, setEditingClientId] = useState(null);
  const [clientDrafts, setClientDrafts] = useState([]);
  const [manualSubmitting, setManualSubmitting] = useState(false);
  const [sensitiveReentryNotice, setSensitiveReentryNotice] = useState('');
  const sessionExpiredNoticeRef = useRef(false);

  const listAgencyId = useMemo(() => {
    if (isPlatformContext) return workspaceAgencyId || undefined;
    return workspaceAgencyId;
  }, [isPlatformContext, workspaceAgencyId]);

  const {
    agencies,
    loading: agenciesLoading,
    error: agenciesError,
  } = usePlatformAgencies({
    enabled: isPlatformContext,
    token,
    workspaceAgencyId,
    setWorkspaceAgencyId,
  });

  const canCreate = isPlatformContext
    ? Boolean(workspaceAgencyId)
    : Boolean(workspaceAgencyId) || isFrontendDevAuthBypassEnabled();

  const newClientDisabledReason = isPlatformContext && !workspaceAgencyId
    ? 'Select an agency first.'
    : '';

  const loadClients = useCallback(async () => {
    if (!isPlatformContext && !workspaceAgencyId) {
      setIsFetchingClients(false);
      setHasResolvedClients(true);
      return;
    }

    const isInitialFetch = !hasResolvedClients;
    if (isInitialFetch) {
      setIsFetchingClients(true);
    }
    setError(null);

    try {
      const result = await fetchFundingClients({
        agencyId: listAgencyId,
        search,
        status: statusFilter,
      });
      const apiClients = result?.clients ?? [];
      const realClients = filterRealFundingClients(apiClients);

      if (realClients.length > 0) {
        setClients(realClients);
        if (clientsSampleMode) {
          saveClientsSampleMode(false);
          setClientsSampleMode(false);
        }
      } else if (isFundingClientsSampleUiEnabled() && clientsSampleMode) {
        setClients(DEV_MOCK_FUNDING_CLIENTS);
      } else if (apiClients.length > 0) {
        setClients(apiClients);
      } else {
        setClients([]);
      }
    } catch (err) {
      if (isFundingClientsSampleUiEnabled()) {
        setClients(clientsSampleMode ? DEV_MOCK_FUNDING_CLIENTS : []);
        setError(null);
      } else {
        setError(err?.message || 'Failed to load funding clients');
        setClients([]);
      }
    } finally {
      setIsFetchingClients(false);
      setHasResolvedClients(true);
    }
  }, [isPlatformContext, workspaceAgencyId, listAgencyId, search, statusFilter, clientsSampleMode, hasResolvedClients]);

  useEffect(() => {
    loadClients();
  }, [loadClients]);

  const isClientsDataPending = isFetchingClients && !hasResolvedClients && clients.length === 0;
  const clientsSkeletonTimedOut = useSkeletonFailsafe(authLoading || isClientsDataPending);
  const shouldShowRosterSkeleton = !clientsSkeletonTimedOut && (authLoading || isClientsDataPending);
  const shouldShowClientDetailsSkeleton = Boolean(
    routeClientId
    && !selectedClient
    && !clientsSkeletonTimedOut
    && (authLoading || isClientsDataPending),
  );

  useEffect(() => {
    setPage(1);
  }, [search, statusFilter, pageSize]);

  const filteredClients = useMemo(() => {
    let list = [...clients];
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter((c) => (
        [c.name, c.businessName, c.email, c.phone, c.clientCode].some((v) => String(v || '').toLowerCase().includes(q))
      ));
    }
    if (statusFilter) {
      list = list.filter((c) => c.status === statusFilter);
    }
    return list;
  }, [clients, search, statusFilter]);

  const paginatedClients = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredClients.slice(start, start + pageSize);
  }, [filteredClients, page, pageSize]);

  const handleRosterStatsChange = useCallback((clientId, stats) => {
    setRosterStats((prev) => ({ ...prev, [clientId]: stats }));
  }, []);

  const openNewClientModal = useCallback(() => {
    setNewClientModalOpen(true);
  }, []);

  useEffect(() => {
    if (pendingNewClientOpenRef.current && viewMode === 'roster') {
      pendingNewClientOpenRef.current = false;
      openNewClientModal();
    }
  }, [viewMode, openNewClientModal]);

  const handleNewClientOption = (optionId) => {
    setNewClientModalOpen(false);
    if (optionId === 'manual') {
      setManualInitialValues(null);
      setManualDrawerOpen(true);
    } else if (optionId === 'paste') {
      setPasteInitialText('');
      setPasteDrawerOpen(true);
    } else if (optionId === 'upload') {
      setUploadDrawerOpen(true);
    } else if (optionId === 'bulk') {
      setBulkImportOpen(true);
    }
  };

  const returnToRoster = useCallback(() => {
    setViewMode('roster');
    setSelectedClient(null);
    navigate(clientsPath || pathFor('clients'), { replace: true });
  }, [navigate, clientsPath, pathFor]);

  const openClientDetails = useCallback((client) => {
    setSelectedClient(client);
    setViewMode('details');
    const base = clientsPath || pathFor('clients');
    navigate(`${base}/${client.id}`, { replace: true });
  }, [navigate, clientsPath, pathFor]);

  const handleClearSampleData = useCallback(() => {
    setClients((prev) => prev.filter((client) => !client.isSample));
    setClientsSampleMode(false);
    saveClientsSampleMode(false);
    if (selectedClient?.isSample) {
      returnToRoster();
    }
    toast.success('Sample data cleared');
  }, [returnToRoster, selectedClient?.isSample]);

  const handleShowSampleData = useCallback(() => {
    if (hasRealClients(clients)) {
      toast.info('Real clients are already loaded. Sample data is only shown when no real clients exist.');
      return;
    }
    setClientsSampleMode(true);
    saveClientsSampleMode(true);
    setClients(DEV_MOCK_FUNDING_CLIENTS);
    toast.success('Sample data restored');
  }, [clients]);

  const showRosterSampleBanner = isFundingClientsSampleUiEnabled()
    && clientsSampleMode
    && hasOnlySampleClients(clients)
    && !hasRealClients(clients);

  const handleNewFundingClientFromDetail = useCallback(() => {
    pendingNewClientOpenRef.current = true;
    returnToRoster();
  }, [returnToRoster]);

  const handleClientUpdate = useCallback((nextClient) => {
    setSelectedClient(nextClient);
    setClients((prev) => prev.map((item) => (item.id === nextClient.id ? nextClient : item)));
  }, []);

  useEffect(() => {
    if (!routeClientId) return;
    const match = filteredClients.find((item) => item.id === routeClientId)
      || clients.find((item) => item.id === routeClientId)
      || (clientsSampleMode && routeClientId.startsWith('fcl_dev_')
        ? DEV_MOCK_FUNDING_CLIENTS.find((item) => item.id === routeClientId)
        : null);
    if (match) {
      setSelectedClient(match);
      setViewMode('details');
      return;
    }

    if (!listAgencyId || routeClientId.startsWith('fcl_local_')) return;

    let cancelled = false;
    fetchFundingClient(routeClientId, listAgencyId)
      .then((client) => {
        if (cancelled || !client) return;
        setSelectedClient(client);
        setViewMode('details');
        setClients((prev) => {
          if (prev.some((item) => item.id === client.id)) {
            return prev.map((item) => (item.id === client.id ? client : item));
          }
          return [client, ...prev];
        });
      })
      .catch(() => {
        if (!cancelled) {
          toast.error('Could not load client details.');
        }
      });

    return () => {
      cancelled = true;
    };
  }, [routeClientId, filteredClients, clients, clientsSampleMode, listAgencyId]);

  const resolveCreateAgencyId = useCallback(() => {
    if (workspaceAgencyId) return workspaceAgencyId;
    if (!isPlatformContext && isFrontendDevAuthBypassEnabled()) {
      return 'agency_internal_001';
    }
    return null;
  }, [workspaceAgencyId, isPlatformContext]);

  const refreshClientDrafts = useCallback(() => {
    const agencyId = resolveCreateAgencyId();
    setClientDrafts(agencyId ? listFundingClientDrafts(agencyId) : []);
  }, [resolveCreateAgencyId]);

  useEffect(() => {
    refreshClientDrafts();
  }, [refreshClientDrafts, workspaceAgencyId, manualDrawerOpen]);

  useEffect(() => {
    const agencyId = resolveCreateAgencyId();
    if (!agencyId || authLoading) return;
    const draft = loadAddClientSessionDraft(agencyId);
    if (!draft?.form) return;
    setManualInitialValues({ ...emptyManualIntakeForm(), ...draft.form });
    setManualDrawerOpen(true);
    setEditingClientId(null);
    if (draft.requiresSensitiveReentry) {
      setSensitiveReentryNotice(
        'Your previous session expired. Non-sensitive fields were restored — please re-enter SSN, EIN, and application password if needed.',
      );
    }
  }, [authLoading, resolveCreateAgencyId]);

  const handleSessionExpiredDuringCreate = useCallback((form) => {
    const agencyId = resolveCreateAgencyId();
    if (agencyId && form) {
      saveAddClientSessionDraft(agencyId, form);
      setManualInitialValues({ ...form });
    }
    if (!sessionExpiredNoticeRef.current) {
      sessionExpiredNoticeRef.current = true;
      toast.error(FUNDING_SESSION_EXPIRED_MESSAGE);
    }
    const returnPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    redirectToLoginForExpiredSession(returnPath);
  }, [resolveCreateAgencyId]);

  const finalizePersistedClient = useCallback(async (createdClient, {
    closeManual = false,
    closeUpload = false,
    closePaste = false,
    draftId = null,
  } = {}) => {
    saveClientsSampleMode(false);
    setClientsSampleMode(false);
    const agencyId = resolveCreateAgencyId();
    if (agencyId) {
      clearAddClientSessionDraft(agencyId);
    }
    setSensitiveReentryNotice('');
    if (draftId) {
      if (agencyId) {
        deleteFundingClientDraft(agencyId, draftId);
        refreshClientDrafts();
      }
      setManualInitialValues(null);
    }
    if (closeManual) {
      setManualDrawerOpen(false);
      setEditingClientId(null);
    }
    if (closeUpload) {
      setUploadDrawerOpen(false);
      setUploadSession(null);
    }
    if (closePaste) {
      setPasteDrawerOpen(false);
      setPasteInitialText('');
    }

    await loadClients();
    const normalized = normalizeFundingClient(createdClient);
    if (normalized?.id) {
      openClientDetails(normalized);
    }
    toast.success('Client created');
  }, [loadClients, openClientDetails, resolveCreateAgencyId, refreshClientDrafts]);

  const persistNewClient = useCallback(async (payload, drawerOptions = {}) => {
    const agencyId = resolveCreateAgencyId();
    if (!agencyId) {
      toast.error('Select an agency before creating a client.');
      return null;
    }

    try {
      const created = await createFundingClient({ ...payload, agency_id: agencyId });
      await finalizePersistedClient(created, drawerOptions);
      return created;
    } catch (err) {
      if (isFundingAuthError(err) || err?.status === 401 || /session has expired/i.test(err?.message || '')) {
        handleSessionExpiredDuringCreate(drawerOptions.formSnapshot || null);
        return null;
      }
      toast.error(err?.message || 'Could not save client.');
      return null;
    }
  }, [resolveCreateAgencyId, finalizePersistedClient, handleSessionExpiredDuringCreate]);

  const handleSaveDraft = useCallback((form) => {
    const agencyId = resolveCreateAgencyId();
    if (!agencyId) {
      toast.error('Select an agency before saving a draft.');
      return;
    }
    try {
      const saved = saveFundingClientDraft(agencyId, {
        form,
        source: form.source || 'Manual intake',
        draftId: form.draftId,
      });
      setManualInitialValues({ ...saved.form, draftId: saved.id });
      refreshClientDrafts();
      toast.success('Draft saved.');
    } catch (err) {
      toast.error(err?.message || 'Could not save draft.');
    }
  }, [resolveCreateAgencyId, refreshClientDrafts]);

  const handleResumeDraft = useCallback((draft) => {
    setManualInitialValues({ ...draft.form, draftId: draft.id });
    setManualDrawerOpen(true);
  }, []);

  const handleDeleteDraft = useCallback((draftId) => {
    const agencyId = resolveCreateAgencyId();
    if (!agencyId || !draftId) return;
    deleteFundingClientDraft(agencyId, draftId);
    refreshClientDrafts();
    if (manualInitialValues?.draftId === draftId) {
      setManualInitialValues(null);
    }
    toast.success('Draft deleted.');
  }, [resolveCreateAgencyId, refreshClientDrafts, manualInitialValues?.draftId]);

  const handleManualCreate = async (form) => {
    if (manualSubmitting) return;
    const agencyId = resolveCreateAgencyId();
    setManualSubmitting(true);

    if (editingClientId) {
      const editAgencyId = selectedClient?.agencyId || agencyId;
      if (!editAgencyId) {
        toast.error('Select an agency before saving client changes.');
        setManualSubmitting(false);
        return;
      }

      try {
        const payload = manualIntakeFormToUpdatePayload(form);
        await updateFundingClient(editingClientId, editAgencyId, payload);
        const fresh = await fetchFundingClient(editingClientId, editAgencyId);
        setClients((prev) => prev.map((item) => (
          item.id === editingClientId ? { ...item, ...fresh } : item
        )));
        if (selectedClient?.id === editingClientId) {
          setSelectedClient(fresh);
        }
        setEditingClientId(null);
        setManualDrawerOpen(false);
        toast.success('Client updated');
      } catch (err) {
        if (isFundingAuthError(err) || err?.status === 401 || /session has expired/i.test(err?.message || '')) {
          handleSessionExpiredDuringCreate(form);
        } else {
          toast.error(err?.message || 'Could not save client.');
        }
      } finally {
        setManualSubmitting(false);
      }
      return;
    }

    if (!agencyId) {
      toast.error('Select an agency before creating a client.');
      setManualSubmitting(false);
      return;
    }

    try {
      // Preserve non-sensitive draft before create so a 401 can restore the form.
      saveAddClientSessionDraft(agencyId, form);
      setManualInitialValues({ ...form });
      const payload = manualIntakeFormToCreatePayload(form, { agencyId });
      await persistNewClient(payload, {
        closeManual: true,
        draftId: form.draftId || null,
        formSnapshot: form,
      });
    } catch (err) {
      if (isFundingAuthError(err) || err?.status === 401 || /session has expired/i.test(err?.message || '')) {
        handleSessionExpiredDuringCreate(form);
      } else {
        toast.error(err?.message || 'Could not save client.');
      }
    } finally {
      setManualSubmitting(false);
    }
  };

  const handleDocumentCreate = async (payload) => {
    if (!payload?.clientData && !payload?.reviewFields?.length) {
      toast.error('No extracted client data available. Continue with manual intake instead.');
      return;
    }

    const agencyId = resolveCreateAgencyId();
    if (!agencyId) {
      toast.error('Select an agency before creating a client.');
      return;
    }
    const extracted = payload?.clientData || payload;
    const hasIdentity = Boolean(
      extracted.firstName?.trim()
      || extracted.lastName?.trim()
      || extracted.ownerName?.trim()
      || extracted.businessLegalName?.trim()
      || extracted.businessName?.trim(),
    );
    if (!hasIdentity) {
      toast.error('Extracted data is missing required client fields. Continue with manual intake instead.');
      return;
    }

    const source = resolveIntakeSource(payload);
    const referenceNote = payload.savePastedReference !== false && payload.pastedText
      ? String(payload.pastedText).trim()
      : '';
    const documentNote = payload.documents?.length
      ? `Reference documents: ${payload.documents.map((doc) => doc.fileName).filter(Boolean).join(', ')}`
      : '';
    const notes = [referenceNote, documentNote].filter(Boolean).join('\n\n') || null;

    const createPayload = extractedClientDataToCreatePayload(extracted, {
      agencyId,
      source,
      notes,
    });

    const closeUpload = Boolean(payload.documents?.length);
    const closePaste = Boolean(payload.pastedText) || source === 'pasted_client_info';
    await persistNewClient(createPayload, { closeUpload, closePaste });
  };

  const handleUploadBackToOptions = useCallback((documents) => {
    skipUploadSessionClearRef.current = true;
    setUploadSession(documents);
    setUploadDrawerOpen(false);
    setNewClientModalOpen(true);
  }, []);

  const handleBulkImport = async () => {
    await loadClients();
  };

  const openEdit = (client) => {
    setManualInitialValues(clientToManualIntakeForm(client));
    setEditingClientId(client.id);
    setManualDrawerOpen(true);
  };

  const handleDelete = async (client) => {
    const confirmed = window.confirm(`Delete ${client.name}? This cannot be undone from the roster.`);
    if (!confirmed) return;

    try {
      if (!isFrontendDevAuthBypassEnabled()) {
        await deleteFundingClient(client.id, client.agencyId);
      }
      setClients((prev) => prev.filter((item) => item.id !== client.id));
      if (selectedClient?.id === client.id) {
        returnToRoster();
      }
      toast.success('Client deleted');
    } catch (err) {
      setClients((prev) => prev.filter((item) => item.id !== client.id));
      if (selectedClient?.id === client.id) {
        returnToRoster();
      }
      toast.success('Client removed locally');
    }
  };

  const intakeModals = (
    <>
      <NewFundingClientModal
        open={newClientModalOpen}
        onOpenChange={setNewClientModalOpen}
        onSelectOption={handleNewClientOption}
      />
      <ManualFundingClientDrawer
        open={manualDrawerOpen}
        onOpenChange={(open) => {
          setManualDrawerOpen(open);
          if (!open) {
            setEditingClientId(null);
            setSensitiveReentryNotice('');
          }
        }}
        mode={editingClientId ? 'edit' : 'create'}
        clientId={editingClientId || selectedClient?.id || ''}
        agencyId={selectedClient?.agencyId || listAgencyId || workspaceAgencyId || ''}
        initialValues={manualInitialValues}
        onSubmit={handleManualCreate}
        onSaveDraft={handleSaveDraft}
        drafts={editingClientId ? [] : clientDrafts}
        onResumeDraft={handleResumeDraft}
        onDeleteDraft={handleDeleteDraft}
        showSampleBanner={Boolean(manualInitialValues?.isSample)}
        submitting={manualSubmitting}
        sensitiveReentryNotice={sensitiveReentryNotice}
        onOpenPaste={editingClientId ? null : () => {
          setManualDrawerOpen(false);
          setPasteInitialText('');
          setPasteDrawerOpen(true);
        }}
      />
      <PasteClientInfoDrawer
        open={pasteDrawerOpen}
        onOpenChange={(open) => {
          setPasteDrawerOpen(open);
          if (!open) setPasteInitialText('');
        }}
        agencyId={listAgencyId || workspaceAgencyId || null}
        initialText={pasteInitialText}
        onCreateClient={handleDocumentCreate}
        onBackToOptions={() => {
          setPasteDrawerOpen(false);
          setNewClientModalOpen(true);
        }}
        onContinueManualIntake={({ pastedText: notesText = '', notes = '' } = {}) => {
          setPasteDrawerOpen(false);
          setManualInitialValues({
            ...emptyManualIntakeForm(),
            source: 'Pasted client info',
            notes: notes || notesText || '',
          });
          setManualDrawerOpen(true);
        }}
      />
      <UploadDocumentsDrawer
        open={uploadDrawerOpen}
        onOpenChange={(open) => {
          setUploadDrawerOpen(open);
          if (!open) {
            if (!skipUploadSessionClearRef.current) {
              setUploadSession(null);
            }
            skipUploadSessionClearRef.current = false;
          }
        }}
        existingClientId={selectedClient?.id || null}
        agencyId={listAgencyId || workspaceAgencyId || null}
        onCreateClient={handleDocumentCreate}
        onBackToOptions={handleUploadBackToOptions}
        onContinueManualIntake={({ documents: retainedDocuments = [] } = {}) => {
          skipUploadSessionClearRef.current = true;
          setUploadSession(retainedDocuments);
          const referenceNames = retainedDocuments.map((doc) => doc.fileName).filter(Boolean);
          setManualInitialValues({
            ...emptyManualIntakeForm(),
            source: 'Document upload',
            notes: referenceNames.length
              ? `Reference documents uploaded: ${referenceNames.join(', ')}`
              : '',
          });
          setUploadDrawerOpen(false);
          setManualDrawerOpen(true);
        }}
        onAuthError={() => {
          if (!sessionExpiredNoticeRef.current) {
            sessionExpiredNoticeRef.current = true;
            toast.error(FUNDING_SESSION_EXPIRED_MESSAGE);
          }
          redirectToLoginForExpiredSession(
            `${window.location.pathname}${window.location.search}${window.location.hash}`,
          );
        }}
        initialDocuments={uploadSession ?? EMPTY_UPLOAD_SESSION}
      />
      <BulkImportClientsModal
        open={bulkImportOpen}
        onOpenChange={setBulkImportOpen}
        existingClients={clients}
        agencyId={workspaceAgencyId || 'agency_internal_001'}
        onImportClients={handleBulkImport}
        onRefreshClients={loadClients}
      />
    </>
  );

  if (shouldShowClientDetailsSkeleton) {
    return (
      <>
        <FundingClientDetailsSkeleton />
        {intakeModals}
      </>
    );
  }

  if (viewMode === 'details' && selectedClient) {
    return (
      <>
        <FundingClientDetailsView
          client={selectedClient}
          clients={filteredClients}
          rosterStats={rosterStats}
          agencyId={listAgencyId}
          onBack={returnToRoster}
          onNavigateClient={(nextClient) => openClientDetails(nextClient)}
          onEdit={openEdit}
          onDelete={handleDelete}
          onRosterStatsChange={handleRosterStatsChange}
          onClientUpdate={handleClientUpdate}
          onClearSampleData={handleClearSampleData}
          onNewFundingClient={handleNewFundingClientFromDetail}
        />
        {intakeModals}
      </>
    );
  }

  return (
    <FundingSectionPage
      title="Funding Clients"
      subtitle="Advisor view of every client moving through the FundGenius HQ pipeline."
      activeStage="intake"
      actions={(
        <div className="flex flex-col items-end gap-1">
          <Button
            className="rounded-xl bg-[var(--fg-accent)] hover:bg-[#126b4f]"
            onClick={openNewClientModal}
            disabled={!canCreate}
            title={newClientDisabledReason || undefined}
            aria-describedby={newClientDisabledReason ? 'funding-clients-new-hint' : undefined}
            data-testid="funding-clients-new"
          >
            New Funding Client
          </Button>
          {newClientDisabledReason ? (
            <span
              id="funding-clients-new-hint"
              className="text-xs text-[var(--fg-muted)]"
              data-testid="funding-clients-new-hint"
            >
              {newClientDisabledReason}
            </span>
          ) : null}
        </div>
      )}
    >
      <div data-testid="funding-clients-roster-view">
        {showRosterSampleBanner ? (
          <FundingSampleBanner
            variant="clients"
            testId="roster-sample-banner"
            title="You're viewing sample funding clients."
            subtitle="Create your first funding client or import a client list to see real workspace data."
            clearActionLabel="Clear sample data"
            secondaryActionLabel="Import Clients"
            primaryActionLabel="New Funding Client"
            clearTestId="roster-sample-clear"
            secondaryTestId="roster-sample-import"
            primaryTestId="roster-sample-new-client"
            onClear={handleClearSampleData}
            onSecondaryAction={() => setBulkImportOpen(true)}
            onPrimaryAction={openNewClientModal}
          />
        ) : null}

        <FundingPanel
          title="Client roster"
          subtitle="Select a client to open the Client Details workspace."
          actions={(
            <div className="flex gap-2">
              <Button variant="outline" className="rounded-xl">Export roster</Button>
              <Button variant="outline" className="rounded-xl bg-[var(--fg-accent-soft)] text-[var(--fg-accent)]">Columns</Button>
            </div>
          )}
        >
          <div className="space-y-4">
            <FundingClientFilters
              search={search}
              status={statusFilter}
              onSearchChange={setSearch}
              onStatusChange={setStatusFilter}
              showAgencyFilter={isPlatformContext}
              agencies={agencies}
              agencyId={workspaceAgencyId || ''}
              onAgencyChange={setWorkspaceAgencyId}
              agenciesLoading={agenciesLoading}
              agenciesError={agenciesError}
            />

            {requiresAgencySelection ? (
              <div className="fg-hint-box" data-testid="funding-clients-agency-required-hint">
                Select an agency first to create funding clients. You can still browse all agencies without selecting one.
              </div>
            ) : null}

            {clientDrafts.length > 0 ? (
              <div className="rounded-xl border border-[var(--fg-line)] bg-[var(--fg-panel-soft)] p-4" data-testid="roster-resume-client-draft">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-[var(--fg-text)]">Resume client draft</p>
                    <p className="text-xs text-[var(--fg-muted)]">Saved manual intake drafts for this agency workspace.</p>
                  </div>
                  <Button
                    variant="outline"
                    className="rounded-xl"
                    data-testid="roster-open-manual-draft-btn"
                    onClick={() => {
                      setManualInitialValues(null);
                      setManualDrawerOpen(true);
                    }}
                  >
                    Open manual intake
                  </Button>
                </div>
                <ul className="mt-3 space-y-2">
                  {clientDrafts.map((draft) => (
                    <li
                      key={draft.id}
                      className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-[var(--fg-line)] bg-white px-3 py-2"
                      data-testid={`roster-client-draft-${draft.id}`}
                    >
                      <div className="min-w-0">
                        <div className="truncate text-sm font-medium text-[var(--fg-text)]">{draft.name}</div>
                        <div className="text-xs text-[var(--fg-muted)]">
                          Updated {new Date(draft.updatedAt).toLocaleString()} · Source: {formatDraftSourceLabel(draft.source)}
                        </div>
                      </div>
                      <div className="flex shrink-0 gap-2">
                        <Button variant="outline" className="rounded-xl" onClick={() => handleResumeDraft(draft)}>
                          Resume
                        </Button>
                        <Button variant="ghost" className="rounded-xl" onClick={() => handleDeleteDraft(draft.id)}>
                          Delete draft
                        </Button>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            {shouldShowRosterSkeleton ? (
              <FundingRosterTableSkeleton rows={7} />
            ) : error ? (
              <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                {error}
              </div>
            ) : filteredClients.length === 0 ? (
              <div className="rounded-xl border border-dashed border-[var(--fg-line)] bg-[var(--fg-panel-soft)] px-4 py-10 text-center">
                <p className="text-sm font-medium text-[var(--fg-text)]">No funding clients yet</p>
                <div className="mt-4 flex flex-wrap items-center justify-center gap-3">
                  {isFundingClientsSampleUiEnabled() && !clientsSampleMode ? (
                    <Button
                      variant="outline"
                      className="rounded-xl"
                      data-testid="roster-show-sample-data"
                      onClick={handleShowSampleData}
                    >
                      Show sample data
                    </Button>
                  ) : null}
                  {canCreate ? (
                    <Button className="rounded-xl bg-[var(--fg-accent)] hover:bg-[#126b4f]" onClick={openNewClientModal}>
                      New Funding Client
                    </Button>
                  ) : null}
                </div>
              </div>
            ) : (
              <>
                <FundingClientRosterTable
                  clients={paginatedClients}
                  rosterStats={rosterStats}
                  onSelectClient={openClientDetails}
                  onApplicationInfo={openClientDetails}
                  onViewClient={openClientDetails}
                />
                <FundingRosterPagination
                  page={page}
                  pageSize={pageSize}
                  totalItems={filteredClients.length}
                  onPageChange={setPage}
                  onPageSizeChange={setPageSize}
                />
              </>
            )}
          </div>
        </FundingPanel>
      </div>

      {intakeModals}
    </FundingSectionPage>
  );
};

export default FundingClientsPage;
